import * as L from 'leaflet';
import 'leaflet.markercluster';
import {
  LeafletDirective,
  LeafletLayersControlDirective,
} from '@bluehalo/ngx-leaflet';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Directive, EventEmitter, Inject, Input, NgZone, Output } from '@angular/core';
import type { OverlayQueryOpts } from '../overlays/shared/overlay-query-opts';
import { OverlayService } from '../overlays/shared/overlay.service';
import { HTMLEncoder } from 'src/app/shared/helpers/html-encoder';
import type { Overlay, OverlayPoint } from '../overlays/shared/overlay';
import type { Subscription } from 'rxjs';
import { LinkHelper } from 'src/app/shared/helpers/link-helper';

@Directive({
  selector: '[mtxOverlayLayer]',
  standalone: false,
})
export class OverlayLayerDirective implements OnInit, OnChanges, OnDestroy {

  @Input()
  public mtxOverlayLayer?: OverlayQueryOpts;

  @Output()
  public overlaysReloaded = new EventEmitter<Overlay[]>();

  private readonly overlaysLayer: { overlay_id: string; layer: L.MarkerClusterGroup }[] = [];

  private ctrl!: L.Control.Layers;

  // value of setTimeout()
  private reloadOverlaysTimeout?: number;

  // reload overlays after 60 seconds
  private readonly reloadOverlayTimerInterval = 60000;

  private reloadOverlaysRequest?: Subscription;

  public constructor(@Inject(LeafletDirective) private readonly leafletDirective: LeafletDirective,
    @Inject(LeafletLayersControlDirective) private readonly leafletLayerControlDirective: LeafletLayersControlDirective,
    @Inject(NgZone) private readonly zone: NgZone,
    @Inject(OverlayService) private readonly overlayService: OverlayService) {
    this.leafletLayerControlDirective.layersControlReady.subscribe(ctrl => {
      this.ctrl = ctrl;
    });
  }

  public ngOnInit(): void {
    this.refreshOverlays();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.mtxOverlayLayer) {
      this.refreshOverlays();
    }
  }

  public ngOnDestroy(): void {
    this.tearDown();
    this.overlaysLayer.forEach(overlayLayer => overlayLayer.layer?.off());
  }

  private tearDown(): void {
    if (this.reloadOverlaysTimeout) {
      clearTimeout(this.reloadOverlaysTimeout);
    }

    this.overlaysLayer?.forEach(overlayLayer => overlayLayer.layer.clearLayers());
    this.overlaysLayer?.forEach(overlayLayer => overlayLayer.layer.remove());

    this.zone?.run(() => this.overlaysReloaded?.emit([]));
  }

  private addOverlayLayer(layerGroup: L.MarkerClusterGroup, name: string): void {
    this.ctrl.addOverlay(layerGroup, HTMLEncoder.encodeHTML(name));
  }


  private refreshOverlays(): void {
    this.reloadOverlaysRequest?.unsubscribe();
    this.reloadOverlaysRequest = undefined;

    // safe guard for no map
    if (!this.leafletDirective?.getMap()) {
      return;
    }

    this.reloadOverlaysRequest = this.overlayService
      .search(this.mtxOverlayLayer ?? { limit: 1000 })
      .subscribe({

        next: overlays => {
          // remove overlays that didnt come back from service
          this.removeOldLayers(overlays.data);

          overlays.data.forEach(overlay => {
            if (!(overlay?.points?.length > 0)) {
              return;
            }

            // build markers for overlay
            const overlayMarkers: L.Marker[] = this.buildOverlayMarkers(overlay);

            const existingLayer = this.overlaysLayer.find(ol => ol.overlay_id === overlay.id);
            if (existingLayer) {
              // update existing layer
              for (const marker of existingLayer.layer.getLayers()) {
                // remove all existing markers
                existingLayer.layer.removeLayer(marker);
              }

              for (const marker of overlayMarkers) {
                // add markers from service
                existingLayer.layer.addLayer(marker);
              }
            } else {
              // add new layer
              const overlayGroup = new L.MarkerClusterGroup({ maxClusterRadius: 40 });
              overlayGroup.addLayers(overlayMarkers);
              this.overlaysLayer.push({ overlay_id: overlay.id, layer: overlayGroup });

              // input sanitized in addOverlayLayer method
              this.addOverlayLayer(overlayGroup, overlay.name);
            }
          });
        },
        error: () => {
          // silently fail
        },
        complete: () => {
          window.clearTimeout(this.reloadOverlaysTimeout);
          this.reloadOverlaysTimeout = window.setTimeout(() => {
            this.refreshOverlays();
          }, this.reloadOverlayTimerInterval);
        },
      });
  }

  private removeOldLayers(newOverlays: Overlay[]): void {
    const overlaysToRemove = this.overlaysLayer.filter(cl => newOverlays.findIndex(nl => nl.id === cl.overlay_id) < 0);

    for (const toRemove of overlaysToRemove) {
      const idx = this.overlaysLayer.findIndex(ol => ol.overlay_id === toRemove.overlay_id);
      this.ctrl.removeLayer(toRemove.layer);
      toRemove.layer.clearLayers();
      this.overlaysLayer.splice(idx, 1);
    }
  }

  private buildOverlayMarkers(overlay: Overlay): L.Marker[] {
    const overlayMarkers: L.Marker[] = [];
    const overlayIcon = this.buildIcon(overlay.color);

    overlay.points.forEach(point => {
      const marker = this.buildMarker(overlayIcon, point);

      this.addNameToMarker(marker, point.name);

      this.addNotesToMarker(marker, point);

      overlayMarkers.push(marker);
    });

    return overlayMarkers;
  }

  private buildIcon(overlayColor: string): L.DivIcon {
    return L.divIcon({
      html: `<i class="ati-map-pin" style="color:#${overlayColor}"></i>`,
      iconSize: [13, 22],
      iconAnchor: [15, 20],
      className: 'overlay-point',
    });
  }

  private buildMarker(icon: L.DivIcon, overlayPoint: OverlayPoint): L.Marker {
    return new L.Marker([
      overlayPoint.location?.coordinates[1] ?? 0,
      overlayPoint.location?.coordinates[0] ?? 0,
    ],
    { icon });
  }

  private addNameToMarker(marker: L.Marker, name: string): void {
    marker
      .bindTooltip(HTMLEncoder.encodeHTML(name), {
        direction: 'top',
        interactive: false,
        offset: [-8, -10],
      })
      .closeTooltip();

    marker.on('mouseout', () => {
      if (marker.isTooltipOpen()) {
        marker.closeTooltip();
      }
    });
  }

  private addNotesToMarker(marker: L.Marker, overlayPoint: OverlayPoint): void {
    const popupBlock = document.createElement('span');
    popupBlock.append(overlayPoint.name ?? '');
    const pre = document.createElement('pre');
    if (overlayPoint.notes && overlayPoint.notes?.length > 0) {
      pre.style.cssText = 'word-wrap: break-word; white-space: pre-wrap;';
      pre.textContent = `\n${overlayPoint.notes}`;
    } else {
      pre.textContent = 'no notes';
    }
    popupBlock.append(pre);
    if (overlayPoint.external_link && overlayPoint.external_link?.length > 0) {
      popupBlock.append('\n');
      popupBlock.append(this.createMoreInfoLink(overlayPoint.external_link));
    }

    marker
      .bindPopup(popupBlock, {
        autoClose: true,
        autoPan: false,
        keepInView: false,
        offset: [-8, -10],
      })
      .closeTooltip();
  }

  private createMoreInfoLink(link: string): HTMLElement {
    const a = document.createElement('a');
    a.href = LinkHelper.verifyLink(link);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = 'More Information';
    return a;
  }

}
